import * as React from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Drawer from "@mui/material/Drawer";
import {
  Stack,
  Box,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchInput from "../../components/SearchInput";
import { useForm } from "react-hook-form";
import { HR } from "../../types/hr";
import { useHRStore } from "../../services/hr";
import { showError } from "../../services/toaster";
import { useCorporateStore } from "../../services/corporates";

export default function AddHRDialog({
  isModalOpen,
  toggleModal,
  selectedId,
  hideAddButton = false,
}: any) {
  const { onCreate, detail, onUpdate } = useHRStore();
  const { allData: corporates, fetchAll } = useCorporateStore();
  const [hRData, setHRData] = React.useState<HR>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    corporateId: "",
    employeeId: "",
    department: "",
    designation: "",
    employmentStatus: "active",
    employmentType: "full-time",
    isActive: true,
  });

  const handleChange = (key: any, value: any) => {
    setHRData({ ...hRData, [key]: value });
  };

  const {
    handleSubmit,
    reset
  } = useForm<HR>();

  const handleClickOpen = () => {
    toggleModal(true);
  };

  const handleClose = () => {
    toggleModal(false);
    setHRData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      corporateId: "",
      employeeId: "",
      department: "",
      designation: "",
      employmentStatus: "active",
      employmentType: "full-time",
      isActive: true,
    });
  };

  const onSubmit = async () => {
    if (
      !hRData.corporateId ||
      !hRData.firstName?.trim() ||
      !hRData.lastName?.trim() ||
      !hRData.employeeId?.trim()
    ) {
      return showError("Corporate, first name, last name, and employee ID are required");
    }
    let result = null;
    if (hRData?._id) {
      result = await onUpdate(hRData);
    } else {
      result = await onCreate(hRData);
    }

    if (result) {
      handleClose();
    }
  };

  const fetchDetail = async (selectedId: string) => {
    try {
      const data = await detail(selectedId);
      if (data) {
        reset(data);
        setHRData({
          employeeId: data.employeeId || "",
          firstName: data.firstName || "",
          lastName: data.lastName || "",
          email: data.email || "",
          phone: data.phone || "",
          corporateId: data.corporateId || "",
          department: data.department || "",
          designation: data.designation || "",
          employmentStatus: data.employmentStatus || "active",
          employmentType: data.employmentType || "full-time",
          isActive: data.isActive !== false,
          _id: data._id,
        });
      }
    } catch (_error) {
      showError("Failed to load HR details");
    }
  };

  React.useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  React.useEffect(() => {
    if (isModalOpen && selectedId) {
      fetchDetail(selectedId);
    } else if (isModalOpen && !selectedId) {
      setHRData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        corporateId: "",
        employeeId: "",
        department: "",
        designation: "",
        employmentStatus: "active",
        employmentType: "full-time",
        isActive: true,
      });
    }
  }, [isModalOpen, selectedId]);

  return (
    <div>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
      >
        <SearchInput />
        {!hideAddButton && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleClickOpen}
          >
            Add HR
          </Button>
        )}
      </Stack>

      <Drawer
        anchor="right"
        open={isModalOpen}
        onClose={handleClose}
      >
        <Box sx={{ width: { xs: '100%', sm: 400 }, p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white' }}>
            <Typography variant="h6">{selectedId ? "Edit HR" : "Add HR"}</Typography>
            <IconButton onClick={handleClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ p: 3, flexGrow: 1, overflowY: 'auto' }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={2}>
                <TextField
                  margin="dense"
                  id="firstName"
                  label="First Name"
                  fullWidth
                  required
                  size="small"
                  variant="outlined"
                  value={hRData.firstName}
                  onChange={(e) => handleChange("firstName", e.target.value)}
                />

                <TextField
                  margin="dense"
                  id="lastName"
                  label="Last Name"
                  fullWidth
                  required
                  size="small"
                  variant="outlined"
                  value={hRData.lastName}
                  onChange={(e) => handleChange("lastName", e.target.value)}
                />

                <TextField
                  margin="dense"
                  id="employeeId"
                  label="Employee ID"
                  fullWidth
                  required
                  size="small"
                  variant="outlined"
                  value={hRData.employeeId}
                  onChange={(e) => handleChange("employeeId", e.target.value)}
                />

                <TextField
                  margin="dense"
                  id="email"
                  label="Email Address"
                  type="email"
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="name@company.com"
                  value={hRData.email || ""}
                  onChange={(e) => handleChange("email", e.target.value)}
                />

                <TextField
                  margin="dense"
                  id="phone"
                  label="Phone no"
                  type="tel"
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="9876543210"
                  value={hRData.phone || ""}
                  onChange={(e) => handleChange("phone", e.target.value)}
                />

                <FormControl fullWidth margin="dense" required>
                  <InputLabel id="hr-corporate-label">Corporate</InputLabel>
                  <Select
                    labelId="hr-corporate-label"
                    label="Corporate"
                    value={hRData.corporateId || ""}
                    onChange={(e) => handleChange("corporateId", e.target.value)}
                  >
                    {corporates.map((corporate: any) => (
                      <MenuItem key={corporate._id} value={corporate._id}>
                        {corporate.companyName || corporate.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  margin="dense"
                  id="department"
                  label="Department"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={hRData.department || ""}
                  onChange={(e) => handleChange("department", e.target.value)}
                />

                <TextField
                  margin="dense"
                  id="designation"
                  label="Designation"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={hRData.designation || ""}
                  onChange={(e) => handleChange("designation", e.target.value)}
                />

                <FormControl fullWidth margin="dense">
                  <InputLabel id="hr-employment-type-label">Employment Type</InputLabel>
                  <Select
                    labelId="hr-employment-type-label"
                    label="Employment Type"
                    value={hRData.employmentType || "full-time"}
                    onChange={(e) => handleChange("employmentType", e.target.value)}
                  >
                    <MenuItem value="full-time">Full Time</MenuItem>
                    <MenuItem value="part-time">Part Time</MenuItem>
                    <MenuItem value="contract">Contract</MenuItem>
                    <MenuItem value="intern">Intern</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth margin="dense">
                  <InputLabel id="hr-status-label">Employment Status</InputLabel>
                  <Select
                    labelId="hr-status-label"
                    label="Employment Status"
                    value={hRData.employmentStatus || "active"}
                    onChange={(e) => handleChange("employmentStatus", e.target.value)}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="terminated">Terminated</MenuItem>
                    <MenuItem value="on-leave">On Leave</MenuItem>
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(hRData.isActive)}
                      onChange={(e) => handleChange("isActive", e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Stack>
            </form>
          </Box>

          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={handleClose} variant="outlined" color="error">Cancel</Button>
            <Button onClick={handleSubmit(onSubmit)} variant="contained" color="primary">
              {selectedId ? "Update" : "Create"}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </div>
  );
}
